/** Configurações › Importação: migração do ADVBOX por planilha (baixar → corrigir → validar → importar). */
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { confirmar } from "@/ui/feedback";
import { Badge, Card, DataTable, ErrorBox, Loading, useAct } from "@/ui/kit";

interface Issue { aba: string; linha: number; coluna: string; mensagem: string }
type Counts = Record<string, { importar: number; jaImportados: number; ignorados: number }>;
interface State {
  fase: "ocioso" | "exportando" | "exportado" | "validado" | "importando" | "importado" | "desfazendo" | "desfeito" | "erro";
  etapa?: string | null; feitos?: number; total?: number; mensagem?: string | null; por?: string | null; atualizadoEm?: string;
  exportadoEm?: string | null; tokenConfigurado: boolean; cadastros?: Record<string, number>;
  conferencia?: { clientes: number; completadosApi: number; completadosProcesso: number; processos: number; tarefas: number; financeiro: number } | null;
  validacao?: { em: string; arquivo: string; contagem: Counts; erros: Issue[]; totalErros: number; avisos: Issue[]; totalAvisos: number } | null;
  resultado?: { em: string; criados: Record<string, number>; ligados: Record<string, number>; falhas: string[] } | null;
  desfeito?: { em: string; removidos: Record<string, number>; mantidos: string[] } | null;
}

const ABAS: Record<string, string> = { pessoas: "Pessoas", processos: "Casos e processos", partes: "Partes", atividades: "Atividades", financeiro: "Financeiro" };
const RUNNING = new Set(["exportando", "importando", "desfazendo"]);
const CAT: Array<[string, string]> = [
  ["usuarios", "Equipe"], ["unidades", "Unidades"], ["areas", "Áreas"], ["tiposAcao", "Tipos de ação"], ["fases", "Fases"],
  ["etapasCaso", "Etapas do caso"], ["etapasProcesso", "Etapas do processo"], ["tiposAtividade", "Tipos de atividade"],
  ["planoContas", "Plano de contas"], ["centrosCusto", "Centros de custo"],
];

export function ImportSettings(): JSX.Element {
  const { data: s, error } = useQuery({
    queryKey: ["importacao"], queryFn: () => api.get<State>("/config/importacao"),
    refetchInterval: (q) => (q.state.data && RUNNING.has(q.state.data.fase) ? 2000 : false),
  });
  const inv = [["importacao"]];
  const exportar = useAct(() => api.post("/config/importacao/exportar"), inv, undefined, "Exportação iniciada");
  const regerar = useAct(() => api.post("/config/importacao/regerar"), inv, undefined, "Gerando a planilha");
  const validar = useAct(async (f: File) => { const fd = new FormData(); fd.append("file", f); return api.upload<State>("/config/importacao/validar", fd); }, inv, undefined, "Planilha validada");
  const importar = useAct(() => api.post("/config/importacao/importar"), inv, undefined, "Importação iniciada");
  const desfazer = useAct(() => api.post("/config/importacao/desfazer"), inv, undefined, "Desfazendo");
  const file = useRef<HTMLInputElement>(null);
  const refreshFile = useRef<HTMLInputElement>(null);
  const atualizar = useAct(async (f: File) => { const fd = new FormData(); fd.append("file", f); await api.uploadDownload("/config/importacao/atualizar-listas", fd, f.name.replace(/\.xlsx$/i, "") + " (listas atualizadas).xlsx"); }, [], undefined, "Planilha com as listas atualizadas baixada");
  const [showWarn, setShowWarn] = useState(false);

  if (!s) return <Card title="Importar do ADVBOX"><ErrorBox error={error} /><Loading /></Card>;
  const running = RUNNING.has(s.fase);
  const v = s.validacao;
  const pct = s.total ? Math.round(((s.feitos ?? 0) / s.total) * 100) : null;
  const err = exportar.error ?? regerar.error ?? validar.error ?? importar.error ?? desfazer.error ?? atualizar.error ?? error;

  return (
    <div className="stack gap-16">
      <Card title="Importar do ADVBOX" actions={<Badge tone={s.fase === "erro" ? "bad" : running ? "info" : s.fase === "importado" ? "ok" : "neutral"}>{FASE[s.fase]}</Badge>}>
        <div className="stack gap-12 small">
          <div>
            A migração é feita por <b>planilha</b>: o sistema baixa tudo do ADVBOX, já consulta o DataJud (órgão, instância e sistema)
            e gera uma planilha com listas suspensas e os problemas marcados. Você corrige e completa no Excel, envia para <b>validar</b>
            (nada é gravado) e, sem erros, <b>importa</b>. Repetir não duplica.
          </div>
          {!s.tokenConfigurado && <div className="warn-box">O token do ADVBOX não está configurado no servidor (<code>ADVBOX_TOKEN</code> no Secrets Manager). Sem ele, só dá para validar e importar uma planilha já gerada.</div>}

          {running && (
            <div className="info-box">
              <b>{s.etapa ?? "Processando"}…</b> {s.total ? `${s.feitos ?? 0} de ${s.total} (${pct}%)` : s.feitos ? `${s.feitos} item(ns)` : ""}
              <div className="muted">Pode sair desta tela; o processo continua no servidor. A consulta ao DataJud leva cerca de 1 s por processo.</div>
            </div>
          )}
          {s.fase === "erro" && s.mensagem && <div className="error-box">{s.mensagem}</div>}

          <Step n={1} title="Baixar do ADVBOX e gerar a planilha" done={!!s.exportadoEm}>
            <div className="row">
              <button className="btn small primary" disabled={running || !s.tokenConfigurado || exportar.isPending} onClick={() => exportar.mutate()}>Baixar do ADVBOX</button>
              <button className="btn small" disabled={running || !s.exportadoEm || regerar.isPending} title="Usa os dados já baixados (ex.: depois de cadastrar a equipe ou o plano de contas)" onClick={() => regerar.mutate()}>Gerar de novo</button>
              <button className="btn small" disabled={!s.exportadoEm || running} onClick={() => void api.download("/config/importacao/planilha", "VG-importacao-ADVBOX.xlsx")}>Baixar planilha</button>
              {s.exportadoEm && <span className="muted">gerada em {fmtDateTime(s.exportadoEm)}</span>}
            </div>
            <div className="muted">
              Antes, cadastre no VG APP (pode ser pela Planilha mestre) a equipe (com o mesmo e-mail do ADVBOX), áreas, tipos de ação, fases, etapas,
              tipos de atividade e plano de contas: as listas da planilha vêm desses cadastros. Na planilha, comece pela aba <b>De-para</b>:
              cada valor do ADVBOX é ligado uma vez ao do VG APP e vale para todas as linhas.
              "Gerar de novo" usa os dados já baixados e o DataJud já consultado (rápido), mas <b>descarta edições</b>; para manter o que já editou, use "Atualizar listas" abaixo.
            </div>
            {s.conferencia && (
              <div className="info-box">
                <b>Conferência do que veio do ADVBOX:</b> {s.conferencia.clientes} clientes da lista do ADVBOX
                {s.conferencia.completadosApi + s.conferencia.completadosProcesso > 0 && <> + <b>{s.conferencia.completadosApi + s.conferencia.completadosProcesso}</b> pessoas citadas em processos que não estavam na lista
                  ({s.conferencia.completadosApi} com cadastro completo buscado no ADVBOX, {s.conferencia.completadosProcesso} só com o que o processo traz — marcadas em Problemas para conferir)</>}
                {" "}· {s.conferencia.processos} processos · {s.conferencia.tarefas} tarefas · {s.conferencia.financeiro} lançamentos.
                <div className="muted">Compare com os totais do ADVBOX. Todas as pessoas citadas em processos entram na aba Pessoas.</div>
              </div>
            )}
            {s.cadastros && (
              <div className="catalog-chips">
                {CAT.map(([k, label]) => {
                  const n = s.cadastros![k] ?? 0;
                  return <span key={k} className={`chip ${n ? "" : "chip-warn"}`} title={n ? undefined : "Nenhum cadastrado: a lista desta coluna ficará vazia"}>{label}: <b>{n}</b>{!n && " ⚠"}</span>;
                })}
              </div>
            )}
            <div className="row">
              <input ref={refreshFile} type="file" hidden accept=".xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) atualizar.mutate(f); e.target.value = ""; }} />
              <button className="btn small" disabled={atualizar.isPending} onClick={() => refreshFile.current?.click()}>{atualizar.isPending ? "Atualizando…" : "Atualizar listas da minha planilha"}</button>
              <span className="muted">Envie a planilha que você já editou: ela volta igual, com as listas e o De-para refeitos com o cadastro atual (nada é gravado).</span>
            </div>
          </Step>

          <Step n={2} title="Corrigir no Excel e validar" done={!!v && v.totalErros === 0}>
            <div className="row">
              <input ref={file} type="file" hidden accept=".xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) validar.mutate(f); e.target.value = ""; }} />
              <button className="btn small primary" disabled={running || validar.isPending} onClick={() => file.current?.click()}>{validar.isPending ? "Validando…" : "Enviar planilha para validar"}</button>
              {v && <span className="muted">{v.arquivo} · {fmtDateTime(v.em)}</span>}
            </div>
            {v && (
              <div className="stack gap-8">
                <DataTable paginate={false} rowKey={(r) => r.k} rows={Object.entries(v.contagem).map(([k, c]) => ({ k, ...c }))} columns={[
                  { key: "a", label: "Aba", render: (r) => ABAS[r.k] ?? r.k },
                  { key: "i", label: "Vão entrar", render: (r) => r.importar },
                  { key: "j", label: "Já importados", render: (r) => r.jaImportados },
                  { key: "g", label: "Fora (Importar = N)", render: (r) => r.ignorados },
                ]} />
                {v.totalErros > 0
                  ? <div className="error-box"><b>{v.totalErros} erro(s)</b> — corrija na planilha e envie de novo.{v.totalErros > v.erros.length && ` Mostrando os ${v.erros.length} primeiros.`}</div>
                  : <div className="ok-box">Nenhum erro. Pode importar.</div>}
                {v.erros.length > 0 && <IssueTable rows={v.erros} />}
                {v.totalAvisos > 0 && (
                  <div>
                    <button className="btn small ghost" onClick={() => setShowWarn(!showWarn)}>{showWarn ? "Esconder" : "Ver"} {v.totalAvisos} aviso(s) (não impedem a importação)</button>
                    {showWarn && <IssueTable rows={v.avisos} />}
                  </div>
                )}
              </div>
            )}
          </Step>

          <Step n={3} title="Importar" done={s.fase === "importado"}>
            <div className="row">
              <button className="btn small primary" disabled={running || !v || v.totalErros > 0 || importar.isPending}
                onClick={() => void confirmar("Importar a planilha validada? O que já foi importado não duplica.").then((y) => y && importar.mutate())}>Importar</button>
              <span className="muted">Depois, lance o <b>saldo inicial</b> de cada conta na data de corte (as baixas antigas não movimentam conta) e rode um backup.</span>
            </div>
            {s.resultado && (
              <div className="stack gap-8">
                <div className="ok-box">
                  Importado em {fmtDateTime(s.resultado.em)}: {Object.entries(s.resultado.criados).map(([k, n]) => `${n} ${k}`).join(" · ")}
                  {s.resultado.ligados.pessoas ? ` · ${s.resultado.ligados.pessoas} pessoa(s) ligada(s) a cadastros existentes` : ""}
                </div>
                {s.resultado.falhas.length > 0 && <div className="warn-box"><b>{s.resultado.falhas.length} linha(s) não entraram:</b>{s.resultado.falhas.slice(0, 50).map((f) => <div key={f}>{f}</div>)}</div>}
              </div>
            )}
          </Step>

          <div className="row">
            <span className="spacer" />
            <button className="btn small ghost danger" disabled={running}
              onClick={() => void confirmar("Desfazer apaga TUDO o que veio do ADVBOX (pessoas, casos, processos, atividades e lançamentos), exceto o que já ganhou vínculos depois. Continuar?").then((y) => y && desfazer.mutate())}>Desfazer importação</button>
          </div>
          {s.desfeito && (
            <div className="muted">Desfeito em {fmtDateTime(s.desfeito.em)}: {Object.entries(s.desfeito.removidos).map(([k, n]) => `${n} ${k}`).join(" · ")}
              {s.desfeito.mantidos.length > 0 && <div className="warn-box">{s.desfeito.mantidos.length} mantido(s): {s.desfeito.mantidos.slice(0, 20).join("; ")}</div>}
            </div>
          )}
          <ErrorBox error={err} />
        </div>
      </Card>
    </div>
  );
}

const FASE: Record<State["fase"], string> = {
  ocioso: "não iniciada", exportando: "exportando…", exportado: "planilha pronta", validado: "validada", importando: "importando…",
  importado: "importada", desfazendo: "desfazendo…", desfeito: "desfeita", erro: "erro",
};

function Step({ n, title, done, children }: { n: number; title: string; done: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <div className="stack gap-8" style={{ borderTop: "1px solid var(--line, #e5e7eb)", paddingTop: 12 }}>
      <div className="row nowrap"><b>{n}. {title}</b>{done && <Badge tone="ok">ok</Badge>}</div>
      {children}
    </div>
  );
}

function IssueTable({ rows }: { rows: Issue[] }): JSX.Element {
  return (
    <DataTable rows={rows} rowKey={(r) => `${r.aba}-${r.linha}-${r.coluna}-${r.mensagem}`} columns={[
      { key: "a", label: "Aba", width: 150, render: (r) => r.aba },
      { key: "l", label: "Linha", width: 70, render: (r) => r.linha || "—" },
      { key: "c", label: "Coluna", width: 200, render: (r) => r.coluna || "—" },
      { key: "m", label: "O que corrigir", render: (r) => r.mensagem },
    ]} />
  );
}
